"use client";

/**
 * Forces an account off an email-address username.
 *
 * The username is a public handle — the live feed and the top-pull board print
 * it — so an address in that field leaks it to every viewer. New accounts are
 * blocked from choosing one at signup; this is the path for the accounts that
 * already did, and it is deliberately not dismissible: any page they land on
 * asks for a real handle first.
 */
import { useEffect, useState } from "react";
import { Button, ErrorMessage } from "@mot-cards/ui";
import { changeUsername, fetchWithAuth, isAuthenticated, syncCurrentUser } from "@/lib/auth";

/** Fired once a forced rename succeeds, so gates deferred behind this one can re-check. */
export const USERNAME_UPDATED_EVENT = "mot:username-updated";

export function UsernameGate() {
  const [needed, setNeeded] = useState(false);
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchWithAuth("/api/auth/me");
        if (!res.ok) return;
        const body = await res.json().catch(() => ({}));
        // This is the one /api/auth/me read every page makes; let it also heal
        // a cached identity that no longer matches the cookie session. Pages
        // read that cache synchronously on mount (the collection page fires
        // its fetch with the stale id before this response lands), so when
        // the identity actually changed the only honest recovery is a
        // reload: every consumer re-reads the corrected identity. The write
        // above makes the next sync a no-op, so this cannot loop.
        if (body?.user && syncCurrentUser(body.user as { id?: unknown; email?: unknown; role?: unknown })) {
          window.location.reload();
          return;
        }
        const current = (body?.user as { username?: string } | undefined)?.username ?? "";
        if (!cancelled && current.includes("@")) setNeeded(true);
      } catch {
        // A failed lookup must not lock anyone out of the site.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = username.trim();
    if (!trimmed) {
      setError("Enter a username.");
      return;
    }
    if (trimmed.includes("@")) {
      setError("Usernames cannot contain @.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await changeUsername(trimmed);
      setNeeded(false);
      window.dispatchEvent(new Event(USERNAME_UPDATED_EVENT));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change username.");
    } finally {
      setBusy(false);
    }
  }

  if (!needed) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#05080a]/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="username-gate-title"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#081420] p-6 shadow-2xl"
      >
        <h2
          id="username-gate-title"
          className="font-[family-name:var(--font-archivo-black)] text-lg font-normal uppercase tracking-[-0.02em] text-white"
        >
          Choose a username
        </h2>
        <p className="mt-2 text-sm leading-snug text-mot-grey">
          Your username is public — it shows on the live feed every time you pull a card. Yours is
          currently your email address, so pick a handle to use instead.
        </p>

        <ErrorMessage id="username-gate-error" className="mt-3 rounded-lg bg-mot-danger/10 px-4 py-2">
          {error}
        </ErrorMessage>

        <label className="mt-3 flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-mot-grey">Username</span>
          <input
            autoFocus
            type="text"
            autoComplete="username"
            maxLength={50}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            aria-describedby={error ? "username-gate-error" : undefined}
            placeholder="Choose a username"
            className="rounded-xl border border-white/15 bg-[#050d15] px-4 py-3 text-sm text-white placeholder:text-mot-grey focus-visible:border-mot-teal focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-mot-teal"
          />
        </label>

        <Button type="submit" variant="primary" disabled={busy} className="mt-4 w-full">
          {busy ? "Saving…" : "Save username"}
        </Button>
      </form>
    </div>
  );
}
